import {
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import {
  TabHeaderComponent,
  TabItem,
} from '../../../shared/components/tab-header/tab-header.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../shared/components/pagination-bar/pagination-bar.component';
import { FleetService } from '../../services/fleet.service';
import { NotificationService } from '../../../services/notification.service';
import { getUserTimezone } from '../../../shared/utils/date.util';

export interface ServiceHistoryModalData {
  fleetId: string;
  fleetName: string;
}

type HistoryTab = 'service' | 'edit' | 'email';

interface ServiceHistoryVehicleRow {
  id: string;
  licensePlate: string;
  package: string;
  lastVisited: string;
}

interface EditHistoryDetail {
  label: string;
  value: string;
}

interface EditHistoryEntry {
  id: string;
  title: string;
  timestamp: string;
  status: 'added' | 'removed' | 'updated';
  details: EditHistoryDetail[];
}

interface EmailRecipientRow {
  id: string;
  recipient: string;
  lastAttempt: string;
  attemptsLabel: string;
  status: 'sent' | 'failed';
  error: string | null;
}

interface EmailHistoryCycle {
  invoiceId: string;
  billingCycle: string;
  invoiceNumber: string;
  hasPdf: boolean;
  recipients: EmailRecipientRow[];
}

const HISTORY_TABS: TabItem[] = [
  { id: 'service', label: 'Service history' },
  { id: 'edit', label: 'Edit history' },
  { id: 'email', label: 'Email history' },
];

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Format parts from an ISO timestamp in the shop/user timezone (same zone sent as
 * the service-history `timezone` query param), not the browser local zone.
 */
function zonedDateParts(
  value: string,
  timeZone: string
): {
  month: string;
  day: number;
  year: number;
  hours: number;
  minutes: string;
  ampm: string;
} | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value || '';
  const month = get('month') || MONTHS_SHORT[date.getUTCMonth()];
  const day = Number(get('day')) || date.getUTCDate();
  const year = Number(get('year')) || date.getUTCFullYear();
  const hours = Number(get('hour')) || 0;
  const minutes = (get('minute') || '00').padStart(2, '0');
  const dayPeriod = (get('dayPeriod') || 'AM').toUpperCase();
  const ampm = dayPeriod.startsWith('P') ? 'PM' : 'AM';
  return { month, day, year, hours, minutes, ampm };
}

/**
 * Format ISO timestamp as "Aug 23, 2026 10:30AM" for the service history table.
 */
function formatVisitTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const parts = zonedDateParts(value, getUserTimezone() || 'UTC');
  if (!parts) return '—';
  return `${parts.month} ${parts.day}, ${parts.year} ${parts.hours}:${parts.minutes}${parts.ampm}`;
}

/**
 * Format ISO timestamp as "Jul 21, 2026 · 4:05 PM" for the edit history timeline.
 */
function formatEditTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const parts = zonedDateParts(value, getUserTimezone() || 'UTC');
  if (!parts) return '—';
  return `${parts.month} ${parts.day}, ${parts.year} · ${parts.hours}:${parts.minutes} ${parts.ampm}`;
}

/**
 * Format a billing period of calendar dates ("YYYY-MM-DD") as "Sep 1 – Sep 30, 2026",
 * or "Dec 1, 2025 – Jan 31, 2026" when the period spans years. Parsed as plain dates so
 * no timezone shift applies.
 */
function formatBillingCycle(start: string | null | undefined, end: string | null | undefined) {
  const parse = (value: string | null | undefined) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    if (!match) return null;
    return {
      year: Number(match[1]),
      month: MONTHS_SHORT[Number(match[2]) - 1],
      day: Number(match[3]),
    };
  };
  const from = parse(start);
  const to = parse(end);
  if (!from || !to || !from.month || !to.month) return '—';
  if (from.year === to.year) {
    return `${from.month} ${from.day} – ${to.month} ${to.day}, ${to.year}`;
  }
  return `${from.month} ${from.day}, ${from.year} – ${to.month} ${to.day}, ${to.year}`;
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** A recipient's latest send run; `sends` > 1 means it was re-sent (e.g. daily retry after a failure). */
function mapEmailRecipientRow(row: any): EmailRecipientRow {
  const attempts = Number(row.attempts) || 1;
  const sends = Number(row.sends) || 1;
  const attemptsLabel = pluralize(attempts, 'attempt');
  return {
    id: String(row.id),
    recipient: row.to_email || '—',
    lastAttempt: formatEditTimestamp(row.last_attempt_at),
    attemptsLabel: sends > 1 ? `${attemptsLabel} · ${pluralize(sends, 'send')}` : attemptsLabel,
    status: row.status === 'sent' ? 'sent' : 'failed',
    error: row.status === 'sent' ? null : row.error || 'Send failed',
  };
}

function mapEmailHistoryCycle(cycle: any): EmailHistoryCycle {
  return {
    invoiceId: String(cycle.fleet_invoice_id),
    billingCycle: formatBillingCycle(cycle.invoice_period_start, cycle.invoice_period_end),
    invoiceNumber: cycle.invoice_number || '—',
    hasPdf: Boolean(cycle.has_pdf),
    recipients: Array.isArray(cycle.recipients) ? cycle.recipients.map(mapEmailRecipientRow) : [],
  };
}

function mapAuditActionToStatus(action: string): EditHistoryEntry['status'] {
  if (action === 'add') return 'added';
  if (action === 'remove') return 'removed';
  return 'updated';
}

function formatDetailValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.map(formatDetailValue).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Modal that displays a fleet's service history (visit stats + per-visit table),
 * edit history (audit timeline) and invoice email history as tabs. Loads data from FleetService.
 */
@Component({
  selector: 'app-service-history-modal',
  standalone: true,
  imports: [CommonModule, TabHeaderComponent, PaginationBarComponent],
  templateUrl: './service-history-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./service-history-modal.component.scss'],
})
export class ServiceHistoryModalComponent {
  private readonly fleetService = inject(FleetService);
  private readonly notificationService = inject(NotificationService);
  private serviceHistorySub: Subscription | null = null;
  private auditLogSub: Subscription | null = null;
  private emailHistorySub: Subscription | null = null;

  @Input() set data(value: ServiceHistoryModalData | null) {
    if (value) {
      this.modalData.set(value);
      this.showModal.set(true);
      this.activeTab.set('service');
      this.currentPage.set(1);
      this.editHistoryLoaded.set(false);
      this.editHistoryEntries.set([]);
      this.emailHistoryLoaded.set(false);
      this.emailHistoryCycles.set([]);
      this.emailCurrentPage.set(1);
      this.emailTotalItems.set(0);
      this.loadServiceHistory();
    }
  }
  @Output() closeModal = new EventEmitter<void>();

  readonly showModal = signal<boolean>(false);
  readonly modalData = signal<ServiceHistoryModalData | null>(null);

  readonly tabs = HISTORY_TABS;
  readonly activeTab = signal<HistoryTab>('service');

  readonly totalVisits = signal(0);
  readonly totalVehicles = signal(0);
  readonly vehicleRows = signal<ServiceHistoryVehicleRow[]>([]);
  readonly serviceHistoryLoading = signal(false);

  readonly currentPage = signal(1);
  readonly itemsPerPage = signal(10);
  readonly totalItems = signal(0);
  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalItems() / this.itemsPerPage()))
  );

  readonly paginationData = computed<PaginationData>(() => ({
    currentPage: this.currentPage(),
    totalPages: this.totalPages(),
    totalItems: this.totalItems(),
    itemsPerPage: this.itemsPerPage(),
  }));

  readonly editHistoryEntries = signal<EditHistoryEntry[]>([]);
  readonly editHistoryLoading = signal(false);
  readonly editHistoryLoaded = signal(false);

  readonly emailHistoryCycles = signal<EmailHistoryCycle[]>([]);
  readonly emailHistoryLoading = signal(false);
  readonly emailHistoryLoaded = signal(false);
  readonly emailCurrentPage = signal(1);
  readonly emailItemsPerPage = signal(10);
  readonly emailTotalItems = signal(0);
  readonly openingPdfId = signal<string | null>(null);

  readonly emailPaginationData = computed<PaginationData>(() => ({
    currentPage: this.emailCurrentPage(),
    totalPages: Math.max(1, Math.ceil(this.emailTotalItems() / this.emailItemsPerPage())),
    totalItems: this.emailTotalItems(),
    itemsPerPage: this.emailItemsPerPage(),
  }));

  onTabChange(tabId: string): void {
    this.activeTab.set(tabId as HistoryTab);
    if (tabId === 'edit' && !this.editHistoryLoaded()) {
      this.loadEditHistory();
    }
    if (tabId === 'email' && !this.emailHistoryLoaded()) {
      this.loadEmailHistory();
    }
  }

  onEmailPageChange(page: number): void {
    this.emailCurrentPage.set(page);
    this.loadEmailHistory();
  }

  onEmailItemsPerPageChange(itemsPerPage: number): void {
    this.emailItemsPerPage.set(itemsPerPage);
    this.emailCurrentPage.set(1);
    this.loadEmailHistory();
  }

  /** Open the invoice's current PDF via a freshly signed URL (never cached client-side). */
  onOpenPdf(cycle: EmailHistoryCycle): void {
    const fleetId = this.modalData()?.fleetId;
    if (!fleetId || !cycle.hasPdf || this.openingPdfId()) return;

    this.openingPdfId.set(cycle.invoiceId);
    this.fleetService.getInvoicePdf(fleetId, cycle.invoiceId).subscribe({
      next: invoice => {
        this.openingPdfId.set(null);
        if (!invoice?.pdf_url) {
          this.notificationService.add({
            severity: 'error',
            summary: 'PDF Unavailable',
            detail: 'No PDF URL was returned for this invoice.',
            life: 4000,
          });
          return;
        }
        window.open(invoice.pdf_url, '_blank', 'noopener,noreferrer');
      },
      error: error => {
        this.openingPdfId.set(null);
        this.notificationService.add({
          severity: 'error',
          summary: 'PDF Unavailable',
          detail: error?.error?.message || error?.message || 'Failed to open invoice PDF.',
          life: 4000,
        });
      },
    });
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.loadServiceHistory();
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage.set(itemsPerPage);
    this.currentPage.set(1);
    this.loadServiceHistory();
  }

  onClose(): void {
    this.cancelPendingRequests();
    this.showModal.set(false);
    this.closeModal.emit();
  }

  onOkay(): void {
    this.onClose();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackById(_: number, item: EditHistoryEntry): string {
    return item.id;
  }

  trackByRowId(_: number, item: ServiceHistoryVehicleRow): string {
    return item.id;
  }

  trackByCycleId(_: number, item: EmailHistoryCycle): string {
    return item.invoiceId;
  }

  trackByRecipientId(_: number, item: EmailRecipientRow): string {
    return item.id;
  }

  private loadServiceHistory(): void {
    const fleetId = this.modalData()?.fleetId;
    if (!fleetId) return;

    this.serviceHistorySub?.unsubscribe();
    this.serviceHistoryLoading.set(true);

    this.serviceHistorySub = this.fleetService
      .getServiceHistory(fleetId, {
        page: this.currentPage(),
        limit: this.itemsPerPage(),
      })
      .subscribe({
        next: (response: any) => {
          this.serviceHistoryLoading.set(false);
          const data = response?.status && response?.data ? response.data : null;
          if (!data) {
            this.resetServiceHistory();
            return;
          }
          this.totalVisits.set(Number(data.total_visits) || 0);
          this.totalVehicles.set(Number(data.total_vehicles) || 0);
          this.totalItems.set(Number(data.pagination?.total) || Number(data.total_visits) || 0);
          const visits = Array.isArray(data.visits) ? data.visits : [];
          this.vehicleRows.set(
            visits.map((visit: any) => ({
              id: String(visit.id ?? `${visit.license_plate}-${visit.visited_at}`),
              licensePlate: visit.license_plate || '—',
              package: visit.package_name || '—',
              lastVisited: formatVisitTimestamp(visit.visited_at),
            }))
          );
        },
        error: () => {
          this.serviceHistoryLoading.set(false);
          this.resetServiceHistory();
        },
      });
  }

  private loadEditHistory(): void {
    const fleetId = this.modalData()?.fleetId;
    if (!fleetId) return;

    this.auditLogSub?.unsubscribe();
    this.editHistoryLoading.set(true);

    this.auditLogSub = this.fleetService.getAuditLog(fleetId, { page: 1, limit: 100 }).subscribe({
      next: (response: any) => {
        this.editHistoryLoading.set(false);
        this.editHistoryLoaded.set(true);
        const rows =
          response?.status && Array.isArray(response?.data?.rows) ? response.data.rows : [];
        this.editHistoryEntries.set(
          rows.map((row: any) => ({
            id: String(row.id),
            title: row.title || 'Change',
            timestamp: formatEditTimestamp(row.created_at),
            status: mapAuditActionToStatus(row.action),
            details: Array.isArray(row.fields)
              ? row.fields.map((field: any) => ({
                  label: field.label || field.field || 'Field',
                  value: formatDetailValue(field.value),
                }))
              : [],
          }))
        );
      },
      error: () => {
        this.editHistoryLoading.set(false);
        this.editHistoryLoaded.set(true);
        this.editHistoryEntries.set([]);
      },
    });
  }

  private loadEmailHistory(): void {
    const fleetId = this.modalData()?.fleetId;
    if (!fleetId) return;

    this.emailHistorySub?.unsubscribe();
    this.emailHistoryLoading.set(true);

    this.emailHistorySub = this.fleetService
      .getEmailHistory(fleetId, {
        page: this.emailCurrentPage(),
        limit: this.emailItemsPerPage(),
      })
      .subscribe({
        next: (response: any) => {
          this.emailHistoryLoading.set(false);
          this.emailHistoryLoaded.set(true);
          const cycles =
            response?.status && Array.isArray(response?.data?.cycles) ? response.data.cycles : [];
          this.emailHistoryCycles.set(cycles.map(mapEmailHistoryCycle));
          this.emailTotalItems.set(Number(response?.data?.pagination?.total) || cycles.length);
        },
        error: () => {
          this.emailHistoryLoading.set(false);
          this.emailHistoryLoaded.set(true);
          this.emailHistoryCycles.set([]);
          this.emailTotalItems.set(0);
        },
      });
  }

  private resetServiceHistory(): void {
    this.totalVisits.set(0);
    this.totalVehicles.set(0);
    this.totalItems.set(0);
    this.vehicleRows.set([]);
  }

  private cancelPendingRequests(): void {
    this.serviceHistorySub?.unsubscribe();
    this.serviceHistorySub = null;
    this.auditLogSub?.unsubscribe();
    this.auditLogSub = null;
    this.emailHistorySub?.unsubscribe();
    this.emailHistorySub = null;
  }
}
